import React, { useState } from 'react';
import { X } from 'lucide-react';

interface PrintDeficienciesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPrint: (options: PrintOptions) => void;
  printing: boolean;
}

export interface PrintOptions {
  title: string;
  subTitle: string;
  imageSize: 'none' | 'small' | 'medium' | 'large';
  showPrivateNotes: boolean;
}

const PrintDeficienciesModal: React.FC<PrintDeficienciesModalProps> = ({
  isOpen,
  onClose,
  onPrint,
  printing
}) => {
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [imageSize, setImageSize] = useState<'none' | 'small' | 'medium' | 'large'>('medium');
  const [showPrivateNotes, setShowPrivateNotes] = useState(false);

  const handlePrint = () => {
    onPrint({
      title,
      subTitle,
      imageSize,
      showPrivateNotes
    });
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10001
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '450px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #E5E7EB'
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#48182F' }}>
            Print Deficiency
          </h2>
          <button
            onClick={onClose}
            disabled={printing}
            style={{
              background: 'none',
              border: 'none',
              cursor: printing ? 'not-allowed' : 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7280',
              fontSize: '18px'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          {/* Title Field */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                color: '#374151'
              }}
            >
              Title <span style={{ color: '#DC2626' }}>*</span>:
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={printing}
              className="form-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px'
              }}
              placeholder="Enter title"
            />
          </div>

          {/* Sub Title Field */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '6px',
                fontSize: '14px',
                color: '#374151'
              }}
            >
              Sub Title <span style={{ color: '#DC2626' }}>*</span>:
            </label>
            <input
              type="text"
              value={subTitle}
              onChange={(e) => setSubTitle(e.target.value)}
              disabled={printing}
              className="form-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px'
              }}
              placeholder="Enter sub title"
            />
          </div>

          {/* Image Size and Show Private Notes Row */}
          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
            {/* Image Size Dropdown */}
            <div style={{ flex: 1 }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '14px',
                  color: '#374151'
                }}
              >
                Image:
              </label>
              <select
                value={imageSize}
                onChange={(e) => setImageSize(e.target.value as 'none' | 'small' | 'medium' | 'large')}
                disabled={printing}
                className="form-input"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px'
                }}
              >
                <option value="none">No Image</option>
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>

            {/* Show Private Notes Checkbox */}
            <div style={{ flex: 1, paddingTop: '28px' }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  color: '#374151',
                  cursor: printing ? 'not-allowed' : 'pointer'
                }}
              >
                <input
                  type="checkbox"
                  checked={showPrivateNotes}
                  onChange={(e) => setShowPrivateNotes(e.target.checked)}
                  disabled={printing}
                  style={{
                    width: '16px',
                    height: '16px',
                    accentColor: '#48182F',
                    cursor: printing ? 'not-allowed' : 'pointer'
                  }}
                />
                Show Private Notes
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 20px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px'
          }}
        >
          <button
            onClick={onClose}
            disabled={printing}
            className="btn btn-secondary btn-md"
            style={{
              opacity: printing ? 0.7 : 1,
              cursor: printing ? 'not-allowed' : 'pointer'
            }}
          >
            Close
          </button>
          <button
            onClick={handlePrint}
            disabled={printing || !title.trim() || !subTitle.trim()}
            className="btn btn-primary btn-md"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: printing || !title.trim() || !subTitle.trim() ? 0.7 : 1,
              cursor: printing || !title.trim() || !subTitle.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {printing ? (
              <>
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #ffffff',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                Printing...
              </>
            ) : (
              'Print'
            )}
          </button>
        </div>

        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default PrintDeficienciesModal;
